import type { Company } from '../@types/Company';
import { PaymentStatus } from '../@types/PaymentStatus';

export type ModuleName = 'communicating' | 'prospecting' | 'monitoring';

// Module type ID mapping
export const MODULE_TYPE_ID = {
  COMMUNICATION: 1,
  PROSPECTING: 2,
  MONITORING: 3,
};

// Map from moduleTypeId (int) to ModuleName (string)
export function getModuleNameFromTypeId(typeId: number | null): ModuleName | null {
  if (!typeId) {return null;}
  
  let result: ModuleName | null = null;
  switch (typeId) {
  case MODULE_TYPE_ID.COMMUNICATION:
    result = 'communicating';
    break;
  case MODULE_TYPE_ID.PROSPECTING:
    result = 'prospecting';
    break;
  case MODULE_TYPE_ID.MONITORING:
    result = 'monitoring';
    break;
  default:
    result = null;
  }
  return result;
}

// Map from ModuleName (string) to moduleTypeId (int)
export function getTypeIdFromModuleName(moduleName: ModuleName | null): number | null {
  if (!moduleName) {return null;}
  
  let result: number | null = null;
  switch (moduleName) {
  case 'communicating':
    result = MODULE_TYPE_ID.COMMUNICATION;
    break;
  case 'prospecting':
    result = MODULE_TYPE_ID.PROSPECTING;
    break;
  case 'monitoring':
    result = MODULE_TYPE_ID.MONITORING;
    break;
  default:
    result = null;
  }
  return result;
}

/**
 * Check if company has an active ProAbono subscription
 * @param company The company object
 * @returns boolean indicating if the company has an active subscription
 */
export function hasActiveSubscription(company: Company | null): boolean {
  if (!company) {
    return false;
  }

  // Check for unlimited free access
  if (company.paymentStatus === PaymentStatus.UNLIMITED_FREE) {
    return true;
  }

  // Check ProAbono payment status - includes all active status variants
  const activeStatuses = [
    PaymentStatus.ACTIVE,
    PaymentStatus.ACTIVE_UNDER_1000,
    PaymentStatus.ACTIVE_1000_TO_2000,
    PaymentStatus.ACTIVE_OVER_2000,
  ];
  
  if (activeStatuses.includes(company.paymentStatus as PaymentStatus) && company.proAbonoSubscriptionEnd) {
    const subscriptionEnd = new Date(company.proAbonoSubscriptionEnd);
    return subscriptionEnd > new Date();
  }

  return false;
}

/**
 * Check if a company has access to a specific module based on its subscription
 * @param company The company object
 * @param moduleName The name of the module to check
 * @deprecated The moduleName parameter is no longer used as all modules are accessible with an active ProAbono subscription. This parameter is kept for API compatibility.
 * @returns boolean indicating if access is granted
 */
export function hasModuleAccess(
  company: Company | null,
  moduleName: ModuleName,
): boolean {
  // moduleName parameter kept for API compatibility - currently all modules accessible with active subscription
  void moduleName;
  
  // Special roles handling would be in the backend
  if (!company) {
    return false;
  }
  
  // ProAbono payment system: single subscription gives access to all modules
  return hasActiveSubscription(company);
}

/**
 * Check if a company has access to a module by its type ID
 * @param company The company object
 * @param moduleTypeId The numeric module type ID
 * @returns boolean indicating if access is granted
 */
export function hasModuleAccessById(
  company: Company | null,
  moduleTypeId: number | null,
): boolean {
  if (!moduleTypeId) {
    return false;
  }
  
  const moduleName = getModuleNameFromTypeId(moduleTypeId);
  
  const result = moduleName ? hasModuleAccess(company, moduleName) : false;
  return result;
}

/**
 * Check if a company has access to any module
 * @param company The company object
 * @returns boolean indicating if access is granted to at least one module
 */
export function hasAnyModuleAccess(company: Company | null): boolean {
  if (!company) {return false;}
  
  // ProAbono payment system - single subscription gives access to all modules
  return hasActiveSubscription(company);
}

/**
 * Get all active modules for a company
 * @param company The company object
 * @returns Array of active module names
 */
export function getActiveModules(company: Company | null): ModuleName[] {
  if (!company) {return [];}
  
  // ProAbono payment system - single subscription gives access to all modules
  if (hasActiveSubscription(company)) {
    return ['communicating', 'prospecting', 'monitoring'];
  }
  
  return [];
}

/**
 * Get subscription status message
 * @param company The company object
 * @returns Human-readable subscription status message
 */
export function getSubscriptionStatusMessage(company: Company | null): string {
  if (!company) {
    return 'No company information';
  }

  if (company.paymentStatus === PaymentStatus.UNLIMITED_FREE) {
    return 'Unlimited free access';
  }

  if (hasActiveSubscription(company)) {
    if (company.proAbonoSubscriptionEnd) {
      const endDate = new Date(company.proAbonoSubscriptionEnd);
      const daysRemaining = Math.ceil((endDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      return `Active until ${endDate.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })} (${daysRemaining} days remaining)`;
    }
    return 'Active subscription';
  }

  if (company.paymentStatus === PaymentStatus.TRIAL) {
    return 'Trial period';
  }

  if (company.paymentStatus === PaymentStatus.SUSPENDED) {
    return 'Subscription suspended';
  }

  if (company.paymentStatus === PaymentStatus.CANCELLED) {
    return 'Subscription cancelled';
  }

  return 'No active subscription';
}

/**
 * Check if subscription is expiring soon (within 7 days)
 * @param company The company object
 * @returns boolean indicating if subscription expires soon
 */
export function isSubscriptionExpiringSoon(company: Company | null): boolean {
  if (!company || !company.proAbonoSubscriptionEnd) {
    return false;
  }

  const endDate = new Date(company.proAbonoSubscriptionEnd);
  const daysRemaining = Math.ceil((endDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  
  return daysRemaining > 0 && daysRemaining <= 7;
} 